import { Box, Icon, Typography } from "@outschool/backpack";
import {
  faCirclePlay,
  faClipboardListCheck,
  faComment,
  faFileAlt,
  faFileVideo,
  faImage,
  faPaperclip,
  faUsers,
} from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import React from "react";

import { ConversationListItem } from "./types";
import {
  getConversationLatestMessage,
  getConversationTimestamp,
  getConversationTitle,
} from "./utils";

function getThreadTypeIcon(item: ConversationListItem) {
  if (item.kind === "feedback") {
    return {
      icon: faComment,
      backgroundColor: "warning.main",
      color: "common.white",
      testId: "thread-type-icon-feedback",
    } as const;
  }

  if (item.kind === "public") {
    return {
      icon: faUsers,
      backgroundColor: "primary.50",
      color: "primary.700",
      testId: "thread-type-icon-public",
    } as const;
  }

  const thread = item.thread;
  if (thread.learningAssignmentClassPost) {
    return {
      icon: faClipboardListCheck,
      backgroundColor: "neutral.100",
      color: "text.secondary",
      testId: "thread-type-icon-assignment",
    } as const;
  }

  if (thread.classPost) {
    const classPost = thread.classPost as { blockType?: string | null };
    return classPost.blockType === "video"
      ? ({
          icon: faCirclePlay,
          backgroundColor: "neutral.100",
          color: "text.secondary",
          testId: "thread-type-icon-video",
        } as const)
      : ({
          icon: faFileAlt,
          backgroundColor: "neutral.100",
          color: "text.secondary",
          testId: "thread-type-icon-text",
        } as const);
  }

  return {
    icon: faComment,
    backgroundColor: "neutral.100",
    color: "text.secondary",
    testId: "thread-type-icon-general",
  } as const;
}

function getMediaPreview(item: ConversationListItem) {
  const content =
    item.kind === "thread"
      ? item.thread.latestMessage?.messageContent
      : item.latestMessage?.messageContent;
  if (!content) {
    return null;
  }
  if (content.video) {
    return { icon: faFileVideo, type: "video" } as const;
  }
  const attachments = content.attachments ?? [];
  if (attachments.length === 0) {
    return null;
  }
  const hasImage = attachments.some(attachment => {
    const file = (attachment as { file?: { mimetype?: string | null } | null })
      .file;
    return (
      typeof file?.mimetype === "string" && file.mimetype.startsWith("image/")
    );
  });
  if (hasImage) {
    return { icon: faImage, type: "photo" } as const;
  }
  return { icon: faPaperclip, type: "attachment" } as const;
}

interface ThreadListItemProps {
  item: ConversationListItem;
  teacherName: string;
  currentLearnerUid?: string;
  onClick: () => void;
}

export default function ThreadListItem({
  item,
  teacherName,
  currentLearnerUid,
  onClick,
}: ThreadListItemProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\ThreadListItem",
  );
  const latestMessage = getConversationLatestMessage(item);
  const unreadCount = item.kind === "thread" ? item.thread.unreadCount : 0;
  const latestText = latestMessage?.messageContent?.text;
  const publicSenderLabel =
    item.kind === "public"
      ? item.latestMessage.sender.uid === currentLearnerUid
        ? t("You")
        : item.latestMessage.sender.name?.trim() || null
      : null;
  const messagePreview =
    publicSenderLabel && latestText
      ? t("{{senderName}}: {{message}}", {
          senderName: publicSenderLabel,
          message: latestText,
        })
      : latestText;
  const mediaPreview = !messagePreview ? getMediaPreview(item) : null;
  const threadTypeIcon = getThreadTypeIcon(item);
  const title = getConversationTitle(item, teacherName, t);
  const timestampValue = getConversationTimestamp(item);
  const timestamp = timestampValue ? dayjs(timestampValue).fromNow() : null;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 12,
        py: 12,
        px: 16,
        borderBottom: "1px solid",
        borderColor: "divider",
        cursor: "pointer",
        transition: "background-color 0.15s",
        "&:hover": { backgroundColor: "action.hover" },
      }}
    >
      <Box
        data-test-id={threadTypeIcon.testId}
        sx={{
          width: 40,
          height: 40,
          borderRadius: "12px",
          backgroundColor: threadTypeIcon.backgroundColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon
          icon={threadTypeIcon.icon}
          sx={{ color: threadTypeIcon.color, fontSize: "1.4rem" }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 4,
          }}
        >
          <Typography
            variant="subtitle2"
            noWrap
            sx={{
              flex: 1,
              fontWeight: 500,
            }}
          >
            {title}
          </Typography>
          {timestamp && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              {timestamp}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
          {messagePreview != null && messagePreview.length > 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{
                flex: 1,
                fontWeight: unreadCount > 0 ? 600 : 400,
              }}
            >
              {messagePreview}
            </Typography>
          ) : mediaPreview ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Icon
                icon={mediaPreview.icon}
                sx={{ fontSize: "1.2rem", color: "text.secondary" }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontStyle: "italic",
                  fontWeight: unreadCount > 0 ? 600 : 400,
                }}
              >
                {mediaPreview.type === "video"
                  ? t("Video")
                  : mediaPreview.type === "photo"
                  ? t("Photo")
                  : t("Attachment")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          {unreadCount > 0 && (
            <Box
              sx={{
                flexShrink: 0,
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                borderRadius: "100%",
                fontSize: "1.4rem",
                fontWeight: 500,
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {unreadCount}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
